import React from 'react';
import type { ChipDetection } from '@/services/chipDetection';
import type { ChipColorEntry } from '@/lib/types';
import { Card } from '@/components/ui';

interface ResultsCardProps {
  detections: ChipDetection[];
  chipColors: ChipColorEntry[];
}

export function ResultsCard({ detections, chipColors }: ResultsCardProps) {
  // Count chips per color
  const colorCounts = new Map<string, number>();
  for (const det of detections) {
    const key = det.className.toLowerCase();
    colorCounts.set(key, (colorCounts.get(key) || 0) + 1);
  }

  // Build chip color map for value lookup
  const valueMap = new Map<string, number>();
  for (const entry of chipColors) {
    valueMap.set(entry.label.toLowerCase(), entry.value);
  }

  const entries = Array.from(colorCounts.entries())
    .map(([color, count]) => {
      const value = valueMap.get(color) ?? 0;
      const config = chipColors.find(c => c.label.toLowerCase() === color);
      return { color, count, value, subtotal: count * value, hex: config?.hex ?? '#999' };
    })
    .sort((a, b) => b.subtotal - a.subtotal);

  const total = entries.reduce((sum, e) => sum + e.subtotal, 0);
  const totalChips = entries.reduce((sum, e) => sum + e.count, 0);

  if (entries.length === 0) return null;

  return (
    <Card className="mt-4">
      <h3 className="text-sm font-semibold text-[#2c2f30] mb-3">Results</h3>

      <div className="space-y-2 mb-4">
        {entries.map(e => (
          <div key={e.color} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
            <div
              className="w-6 h-6 rounded-full border border-gray-200 flex-shrink-0"
              style={{ backgroundColor: e.hex }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#2c2f30] capitalize">{e.color}</div>
              <div className="text-xs text-[#6b7280]">
                {e.count} chip{e.count !== 1 ? 's' : ''} × ${e.value.toFixed(2)}
              </div>
            </div>
            <div className="text-sm font-bold text-[#2c2f30]">${e.subtotal.toFixed(2)}</div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-3 border-t-2 border-[#006855]/20">
        <div>
          <span className="text-xs text-[#6b7280]">{totalChips} chip{totalChips !== 1 ? 's' : ''} detected</span>
        </div>
        <div className="text-xl font-black text-[#006855]">${total.toFixed(2)}</div>
      </div>
    </Card>
  );
}
